import type { Fields } from '@puckeditor/core';
import { CategoriesForMenu, SubcategoryOptions } from 'shared/constants/products';
import { MenuSectionType } from 'src/gql/graphql';

import { MenuSortOptions } from 'shared/constants/dispensaries';

import { brandPickerField } from '../../fields/widgets/brand-picker-field';
import { collectionPickerField } from '../../fields/widgets/collection-picker-field';
import { productPickerField } from '../../fields/widgets/product-picker-field';
import { selectField } from '../../fields/widgets/select-field';

/**
 * The type list for the consolidated Product Carousel block.
 *
 * Values are the platform's existing `MenuSectionType` rather than a parallel
 * CMS-only vocabulary, so a Puck-authored carousel and a Customize-authored menu
 * section describe themselves the same way. `IMAGE` is deliberately absent: it is
 * an image banner rather than a product carousel, and already has its own block.
 *
 * Every type is declared here from the outset even though the renderers land one
 * ticket at a time (ECOMM-12683 onwards). Fixing the whole enum up front means no
 * later change has to migrate a stored `carouselType`, which is the one thing that
 * would be genuinely hard to get right — Puck does not merge `defaultProps` in the
 * published Render path, so a stored value is all a live page has to go on.
 */
export type ProductCarouselType = Exclude<MenuSectionType, MenuSectionType.image>;

/**
 * `carouselType` is typed as a plain string rather than a narrowed union because Puck
 * derives a field's type from the prop it is bound to, and the shared `selectField`
 * widget produces a `CustomField<string>`. It is also the more honest type: saved page
 * data is JSON that an AI edit or a hand-edit can author, so an arbitrary string really
 * can arrive here. `showHeading` is a boolean predicate and its render-time comparison
 * deliberately accepts only literal `false`; malformed saved JSON uses the default of
 * showing the heading.
 *
 * `category` stores the category `value` from `CategoriesForMenu` (e.g. `'Flower'`,
 * `'Pre-Rolls'`, `'Concentrate'`) because that is what `CategoryCarousel`,
 * `SubcategoryCarousel`, and `SubcategoryOptions` all index by.
 */
export type ProductCarouselProps = {
  brandId: string;
  brandName: string;
  category: string;
  carouselType: string;
  collectionSlug: string;
  collectionTitle: string;
  heading: string;
  productIds: string[];
  productNames: string[];
  showHeading: boolean;
  sortBy: string;
  subcategory: string;
  viewAllHref?: string;
  viewAllLabel?: string;
};

type ProductCarouselTypeDefinition = {
  /** Author-facing name in the Type dropdown. */
  label: string;
  /**
   * Heading used when the author leaves the Heading field empty.
   *
   * Takes the whole prop bag so that the types whose heading depends on another
   * field — "Popular {category}", the chosen brand's name, the chosen collection's
   * name — can derive it once those fields exist. Omitted where the heading can only
   * come from a field the type does not have yet, in which case a blank Heading
   * simply produces no heading.
   */
  autoHeading?: (props: ProductCarouselProps) => string;
  /**
   * Additional Puck fields shown only when this type is selected, surfaced via
   * `resolveFields`. Omit for types that need no extra configuration beyond the
   * shared heading controls.
   *
   * This declarative contribution keeps `resolveFields` in `product-carousel.tsx`
   * free of per-type `if`-chains: it merges whatever each type contributes without
   * knowing what those fields are. Brand/Collection/Custom (ECOMM-12685/86/87) add
   * their own `extraFields` here without touching `resolveFields` itself.
   *
   * Typed as `Partial` because each type only contributes its own subset of fields;
   * `resolveFields` merges these onto the base field set, which already covers the
   * shared fields (carouselType, heading, showHeading).
   */
  extraFields?: Partial<Fields<ProductCarouselProps>>;
  /**
   * Returns `true` when the block has enough author-supplied configuration to render
   * meaningfully. Omit for types that require no extra fields — they are always
   * considered configured.
   *
   * When the predicate returns `false`:
   * - On the storefront (`!puck.isEditing`): the block renders nothing, preventing
   *   doomed product queries with empty/undefined filter values.
   * - In the editor: an `EditorPlaceholder` is shown instead of the delegate
   *   carousel, with a message derived from `missingFieldsMessage`.
   *
   * Brand/Collection/Custom (ECOMM-12685/86/87) add their own `isConfigured` here
   * without touching the block's render body.
   */
  isConfigured?: (props: ProductCarouselProps) => boolean;
  /**
   * Human-readable description of what the author still needs to supply, shown in
   * the editor placeholder when `isConfigured` returns `false`.
   *
   * E.g. `"a category"` → "Choose a category in the settings panel."
   * Required whenever `isConfigured` is present.
   */
  missingFieldsMessage?: string;
  /** Editor guidance when the configured carousel has no content for the previewed store. */
  emptyMessage?: string;
};

/**
 * Select options for the Category field, derived from the canonical `CategoriesForMenu`
 * list. Stored value is the category `value` (e.g. `'Flower'`) so it matches the key
 * `SubcategoryOptions` and the carousel components both index by.
 */
export const CATEGORY_OPTIONS = CategoriesForMenu.map(({ label, value }) => ({ label, value }));

/**
 * Derives the subcategory select options for a given category value. Returns an empty
 * array when the category has no subcategories or is unrecognised.
 */
export function subcategoryOptionsForCategory(categoryValue: string): Array<{ label: string; value: string }> {
  const subs = SubcategoryOptions[categoryValue];
  if (!subs || subs.length === 0) {
    return [];
  }
  return subs.map(({ label, value }) => ({ label, value }));
}

/**
 * Resolves a stored subcategory value only if it is valid for the currently selected
 * category. Returns an empty string when the subcategory is stale (left over from a
 * different category) or unrecognised.
 *
 * Used in both the auto-heading derivation and the renderer so a stale stored value
 * can never produce a wrong heading or trigger a wrong query.
 */
export function resolveSubcategory(categoryValue: string, subcategoryValue: string): string {
  if (!subcategoryValue) {
    return '';
  }
  const subs = SubcategoryOptions[categoryValue];
  if (!subs) {
    return '';
  }
  const match = subs.find((s) => s.value === subcategoryValue);
  return match ? match.value : '';
}

/**
 * Sort options for the Collection type, derived from the canonical `MenuSortOptions` so the CMS
 * cannot drift from the sorts the storefront menu already offers. `key` is the stored value
 * because that is what `useCollectionCarousel` forwards as `sortBy`; entries carry a `value` only
 * where it happens to duplicate the key.
 *
 * 'default' is relabelled from "Featured" to name what it actually does here -- within a single
 * collection it means the collection's own configured order, not a store-wide featured ranking.
 */
export const COLLECTION_SORT_OPTIONS = MenuSortOptions.map(({ key, label }) => ({
  label: key === 'default' ? "Featured (collection's own order)" : label,
  value: key,
}));

/**
 * Coerces the stored `productIds` to a list of ids.
 *
 * The declared type says `string[]`, but the value that actually arrives is whatever the saved page
 * JSON holds -- an AI edit or a hand-edit can author a string, a null, or an array with holes in it.
 * The published render path has no error boundary, so reading `.length` off a non-array would take
 * the whole storefront page down rather than just this block.
 */
export function storedProductIds(productIds: unknown): string[] {
  return Array.isArray(productIds) ? productIds.filter((id): id is string => typeof id === 'string' && id !== '') : [];
}

/** Category field definition, shared by both the category and subcategory types. */
const categoryField = selectField(
  'Category',
  CATEGORY_OPTIONS,
  'The product category this carousel shows. Required — changing the category also clears any selected subcategory.'
);

/**
 * The static `autoHeading` strings match the titles the underlying storefront
 * carousels already hardcode, so an author who leaves Heading blank gets the
 * heading the storefront renders today.
 */
export const PRODUCT_CAROUSEL_TYPES: Readonly<Record<ProductCarouselType, ProductCarouselTypeDefinition>> = {
  [MenuSectionType.category]: {
    label: 'Popular Category',
    autoHeading: ({ category }: ProductCarouselProps) => {
      const match = CATEGORY_OPTIONS.find((opt) => opt.value === category);
      return match ? `Popular ${match.label}` : '';
    },
    extraFields: {
      category: categoryField,
    },
    isConfigured: ({ category }: ProductCarouselProps) => category !== '',
    missingFieldsMessage: 'a category',
  },
  [MenuSectionType.subcategory]: {
    label: 'Popular Subcategory',
    autoHeading: ({ category, subcategory }: ProductCarouselProps) => {
      const catMatch = CATEGORY_OPTIONS.find((opt) => opt.value === category);
      if (!catMatch) {
        return '';
      }
      const validSubcategory = resolveSubcategory(category, subcategory);
      if (!validSubcategory) {
        return `Popular ${catMatch.label}`;
      }
      const subOpts = subcategoryOptionsForCategory(category);
      const subMatch = subOpts.find((opt) => opt.value === validSubcategory);
      return subMatch ? `Popular ${subMatch.label}` : `Popular ${catMatch.label}`;
    },
    extraFields: {
      category: categoryField,
      // Subcategory options are derived from the selected category at resolveFields time.
      // The field is rebuilt each time the category changes, so the dropdown stays in sync.
      subcategory: selectField(
        'Subcategory',
        [],
        'The subcategory to feature. Options depend on the selected Category.'
      ),
    },
    isConfigured: ({ category, subcategory }: ProductCarouselProps) =>
      category !== '' && resolveSubcategory(category, subcategory) !== '',
    missingFieldsMessage: 'a category and subcategory',
  },
  // ECOMM-15146: declared for enum completeness only (same as recentlyViewed etc). Not added to
  // AUTHORABLE_PRODUCT_CAROUSEL_TYPE_ORDER/PRODUCT_CAROUSEL_RENDERERS -- not a real Puck block type.
  [MenuSectionType.bundles]: { label: 'Bundles', autoHeading: () => 'Bundles' },
  [MenuSectionType.recentlyViewed]: { label: 'Recently Viewed', autoHeading: () => 'Recently Viewed' },
  [MenuSectionType.specials]: { label: 'Items on Sale', autoHeading: () => 'Items on Sale' },
  [MenuSectionType.topSellers]: { label: 'Top Sellers', autoHeading: () => 'Top Sellers' },
  [MenuSectionType.staffPicks]: {
    label: 'Staff Picks',
    autoHeading: () => 'Staff Picks',
    emptyMessage:
      'Staff Picks has no matching products for this store. Flag available products as Staff Picks to show them here.',
  },
  [MenuSectionType.offers]: { label: 'Offers', autoHeading: () => 'Offers' },
  // Added to develop by PF-5771 (#22039) after this branch forked. Kept here so rebasing does not
  // silently drop a type that already ships -- it is platform-decided, so it needs no extraFields.
  [MenuSectionType.recommendedForYou]: {
    label: 'Recommended For You',
    autoHeading: () => 'Recommended For You',
  },
  [MenuSectionType.brand]: {
    label: 'Brand',
    autoHeading: ({ brandName }: ProductCarouselProps) => brandName || '',
    extraFields: {
      brandId: brandPickerField,
    },
    isConfigured: ({ brandId }: ProductCarouselProps) => brandId !== '',
    missingFieldsMessage: 'a brand',
    emptyMessage:
      'The selected brand may be unavailable or have no matching products at this store. Choose a brand with available products.',
  },
  [MenuSectionType.collection]: {
    label: 'Collection',
    // The picker stores the collection's title alongside its slug, so the heading needs no
    // second query. Falls back to nothing rather than the raw slug, which is a URL fragment
    // and not author-facing copy.
    autoHeading: ({ collectionTitle }: ProductCarouselProps) => collectionTitle || '',
    // `collectionTitle` is deliberately absent: the picker maintains it in the same dispatch as
    // the slug, and an author editing the name independently would break that pairing. It is
    // declared in the static `fields` for TypeScript and stripped by `resolveFields`, so leaving
    // it out here is what keeps it out of the panel.
    extraFields: {
      collectionSlug: collectionPickerField,
      sortBy: selectField(
        'Sort by',
        COLLECTION_SORT_OPTIONS,
        "How products are ordered within the collection. 'Featured' uses the collection's own order."
      ),
    },
    isConfigured: ({ collectionSlug }: ProductCarouselProps) => collectionSlug !== '',
    missingFieldsMessage: 'a collection',
  },
  [MenuSectionType.custom]: {
    label: 'Custom',
    // No `autoHeading`: a hand-picked list has no name of its own to borrow, so a blank Heading
    // renders no heading rather than inventing one from the products.
    //
    // `productNames` is deliberately absent from `extraFields`: the picker maintains it in the same
    // dispatch as `productIds` purely so the chosen list can be shown back to the author without a
    // second query. It is declared in the static `fields` for TypeScript and stripped by
    // `resolveFields`, so leaving it out here is what keeps it out of the panel.
    extraFields: {
      productIds: productPickerField,
    },
    isConfigured: ({ productIds }: ProductCarouselProps) => storedProductIds(productIds).length > 0,
    missingFieldsMessage: 'at least one product',
  },
};

/**
 * The empty option. A carousel's type drives every other decision the block makes,
 * so a freshly dropped block asks the author to choose rather than silently picking
 * a type on their behalf.
 */
export const BLANK_CAROUSEL_TYPE_OPTION = { label: 'Select a type…', value: '' };

const AUTHORABLE_PRODUCT_CAROUSEL_TYPE_ORDER: ProductCarouselType[] = [
  MenuSectionType.topSellers,
  MenuSectionType.category,
  MenuSectionType.subcategory,
  MenuSectionType.recentlyViewed,
  MenuSectionType.recommendedForYou,
  MenuSectionType.specials,
  MenuSectionType.staffPicks,
  MenuSectionType.offers,
  MenuSectionType.brand,
  MenuSectionType.collection,
  MenuSectionType.custom,
];

/** Workshop order for authorable sources. */
export const PRODUCT_CAROUSEL_TYPE_OPTIONS = [
  BLANK_CAROUSEL_TYPE_OPTION,
  ...AUTHORABLE_PRODUCT_CAROUSEL_TYPE_ORDER.map((value) => ({
    label: PRODUCT_CAROUSEL_TYPES[value].label,
    value,
  })),
];

/**
 * Look up a stored `carouselType`, or `undefined` when it is blank or unrecognised.
 *
 * The own-property guard is load-bearing rather than defensive: without it a stored
 * value of `constructor` or `toString` would resolve to something off
 * `Object.prototype` instead of missing. Same rationale as `interpolate`'s guard.
 */
export function carouselTypeDefinition(carouselType: string): ProductCarouselTypeDefinition | undefined {
  return Object.prototype.hasOwnProperty.call(PRODUCT_CAROUSEL_TYPES, carouselType)
    ? PRODUCT_CAROUSEL_TYPES[carouselType as ProductCarouselType]
    : undefined;
}

/**
 * The heading to render, or `undefined` when no heading should render at all.
 *
 * A blank heading and a hidden heading are deliberately different states. Blank
 * falls back to the type's generated heading, which is what makes the Heading field
 * feel pre-populated without actually writing generated text into saved page data —
 * so a carousel whose type later changes re-derives its heading instead of keeping a
 * stale one. Suppressing the heading entirely is the separate `showHeading` toggle,
 * because a carousel with no heading is a real layout choice and not just an empty
 * string.
 */
export function resolveCarouselHeading(props: ProductCarouselProps): string | undefined {
  // Saved page data is unvalidated JSON. Only a literal boolean false opts out;
  // undefined, null, legacy strings, numbers, and objects all use the safe default.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare -- runtime JSON is not type-safe
  if (props.showHeading === false) {
    return undefined;
  }
  // `String(...)` rather than `.trim()` directly: these props are typed, but the value
  // that actually arrives is whatever is in the saved page JSON, which an AI plugin or
  // a hand-edit can author. A stored `heading: 42` would otherwise throw here, and the
  // published render path has no error boundary — one malformed block would take down
  // the whole storefront page rather than just itself.
  // The declared type says this is always a string; the runtime value is whatever the
  // saved JSON holds, which is the whole point of the coercion above.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const authored = String(props.heading ?? '').trim();
  if (authored) {
    return authored;
  }
  const auto = carouselTypeDefinition(props.carouselType)?.autoHeading?.(props);
  // autoHeading can return '' when required fields (category, subcategory) are not yet
  // chosen; treat that the same as "no auto-heading" so the block shows no title rather
  // than an empty one. `||` is intentional: `?? undefined` would pass '' through.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return auto || undefined;
}
